import { PaymentRequestType, PaymentType, Seat, Ticket } from '.';

export interface TransactionResult {
  limit: number;
  page: number;
  transactions: TransactionList[];
  total_count: number;
}

export interface TransactionList {
  id: number;
  pos_ticket_id: number;
  ticket_uuid: string;
  amount: number;
  created_at: string;
  type: PaymentRequestType;
  diner_id: number;
  diner_name: string;
  pos_location_id: number;
  department_id?: number;
  payment_gateway_transaction_id?: number;
  payment_location_id?: number;
  trancloud_transaction_id?: number;
  pos_charge_account_id?: number;
  pos_gift_card_id?: number;
  is_meal_plan?: boolean;
  tip_amount?: number;
}

export interface TransactionRefund {
  amount: number;
  created_at: string;
}

/** Transaction */
export interface Transaction {
  ticket?: Ticket;
  seat?: Seat;
  totals?: TransactionTotal;
  isCompletedDebitTicket?: boolean;
}

export interface TransactionTotal {
  gross: number; // total_base_price
  net: number;
  discount: number; // total_discounts
  taxes: number; // tax_total
  final: number; // calculated_total
  outstanding: number; // new property outstanding_balance
  point_deduction?: number;
  changeDueAmount?: number;
}

export interface TransactionFilter {
  pos_location_id: number;
  pos_operator_id: number;
  diner_name: string;
  date_from: string;
  date_to: string;
  pos_ticket_id: string;
  type: PaymentType;
  credit_card_last_digits: string;
}

/**
 * POS-972 On-Screen Signature
 *
 * `AWAITING`: no signature captured yet, capture step should be shown when required.
 * `SIGNED`: guest signed on-screen, `signature_data` was PATCHed successfully.
 * `PAID_WITHOUT_SIGN`: operator used the "Pay without Sign" escape hatch, no confirmation ever shown.
 * `NOT_REQUIRED`: the transaction never required a signature in the first place — distinct from
 * `AWAITING` so a report reading this field alone can't misread a never-required transaction as one
 * still pending a decision. The POS app never needs to branch on this value itself (it always checks
 * `signature_required` first), but the field can still carry it since the backend returns it.
 */
export enum SignatureStatus {
  AWAITING = 'awaiting',
  SIGNED = 'signed',
  PAID_WITHOUT_SIGN = 'paid_without_sign',
  NOT_REQUIRED = 'not_required'
}

/** Request body for `PATCH .../transactions/:id/signature` and the meal-plan-transaction equivalent. */
export interface SignatureCaptureRequest {
  signature_data?: string;
  skip?: boolean;
}

/** Response shape re-rendered by the transaction/meal-plan-transaction signature PATCH endpoints. */
export interface SignatureCaptureResult {
  id?: number;
  signature_required?: boolean;
  has_signature?: boolean;
  signature_status?: SignatureStatus;
  signature_captured_at?: string;
}
